<template>
  <Menubar
    v-if="
      settings?.recommendationsPageOptions?.menuDisplay !==
        MenuDisplayOption.Hidden
    "
    ref="menuBarEl"
    :model="
      items.map((item) => ({
        label:
          typeof item.to === 'string'
            ? $t('menu.' + item.to)
            : $t('menu.quote'),
        ...item,
      }))
    "
    aria-label="Menu"
    :pt="{ menuitem: { 'aria-level': null } }"
  >
    <template #start>
      <BerwowLogo
        class="berwow-icon"
        role="heading"
        @click="showPopup()"
      />
    </template>
    <template #item="slotProps">
      <router-link
        v-if="slotProps.item.to"
        :to="slotProps.item.to"
        @click="
          typeof slotProps.item.to === 'string'
            ? linkItemClicked(slotProps.item.to)
            : ''
        "
      >
        <div class="p-menuitem-link">
          <div class="icon p-menuitem-icon">
            <GammaIcon
              :src="
                getIconFromCSS(
                  typeof slotProps.item.to === 'string'
                    ? slotProps.item.to
                    : 'additional'
                )
              "
              :default-src="getIcon(slotProps.item.to as string)"
              :icon-class="
                typeof slotProps.item.to === 'string'
                  ? slotProps.item.to
                  : 'additional'
              "
            />
          </div>
          <span class="label p-menuitem-text">{{ slotProps.item.label }}</span>
        </div>
      </router-link>
    </template>
    <template #end>
      <div class="menubar-end">
        <BerwowLogo
          class="berwow-icon"
          @click="showPopup()"
        />
        <ScheduleButton />
      </div>
    </template>
  </Menubar>
</template>
<script lang="ts">
import {defineComponent} from "vue";
import {Component, DefineComponent, Ref, ref, watch} from "vue";
import Menubar from "primevue/menubar";
import BerwowLogo from "@/components/icons/berwow-logo.vue";
import ScheduleButton from "@/components/result/ScheduleButton.vue";
import type { MenuItem } from "primevue/menuitem";
import {useRoute, useRouter} from "vue-router";
import {useLoan, usePassport, useRecommendations, useSelections, useSettings,} from "@/store";
import {BerwowEvent, emit, getIconFromCSS, LayoutEvent, on, QuoteOrigin, TrackingEvent,} from "@/helpers";
import {useConfirm} from "primevue/useconfirm";
import GammaIcon from "@/components/icons/GammaIcon.vue";
import {MenuDisplayOption} from "@/types";
import {storeToRefs} from "pinia";
import {t} from "@/helpers/locale";
import RecommendationsIcon from "@/components/icons/menu/recommendations-icon.vue";
import FinancialDetailsIcon from "@/components/icons/menu/financial-details-icon.vue";
import BerMapIcon from "@/components/icons/menu/ber-map-icon.vue";
import ScheduleSurveyIcon from "@/components/icons/menu/schedule-survey-icon.vue";
import CarbonFootprintIcon from "@/components/icons/menu/carbon-footprint-icon.vue";

export default defineComponent({
  name: "ResultNav",
  components: {
    Menubar,
    BerwowLogo,
    ScheduleButton,
    GammaIcon,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loan = useLoan();
    const passport = usePassport();
    const selections = useSelections();
    const recommendations = useRecommendations();
    const settingsStore = useSettings();
    const {settings, clientSessionId} = storeToRefs(settingsStore);

    const menuBarEl = ref<DefineComponent | undefined>();

    const menuItems: Map<string, any> = new Map([
      [
        MenuDisplayOption.Recommendations,
        {
          class:
              route.name === "recommendations"
                  ? "recommendations active"
                  : "recommendations",
          to: "recommendations",
        },
      ],
      [
        MenuDisplayOption.FinancialDetails,
        {
          to: "financial-detail",
          class:
              route.name === "financial-detail"
                  ? "financial-detail active"
                  : "financial-detail",
        },
      ],
      [
        MenuDisplayOption.CarbonFootprint,
        {
          class:
              route.name === "carbon-footprint"
                  ? "carbon-footprint active"
                  : "carbon-footprint",
          to: "carbon-footprint",
        },
      ],
      [
        "berMap",
        {
          class: route.name === "ber-map" ? "ber-map active" : "ber-map",
          to: "ber-map",
        },
      ],
      [
        MenuDisplayOption.CostsAndSavings,
        {
          class:
              route.name === "costs-and-savings"
                  ? "costs-and-savings active"
                  : "costs-and-savings",
          to: "costs-and-savings",
        },
      ],
      [
        MenuDisplayOption.Analysis,
        {
          class: route.name === "analysis" ? "analysis active" : "analysis",
          to: "analysis",
        },
      ],
      [
        MenuDisplayOption.BRPassport,
        {
          class: route.name === "brp" ? "brp active" : "brp",
          to: "brp",
        },
      ],
      [
        MenuDisplayOption.ActionButton,
        {
          to: route,
          command: () =>
              recommendations.quoteRecommendations(QuoteOrigin.MenuItem),
        },
      ],
    ]);
    const items: Ref<MenuItem[]> = ref(
        [
          menuItems.get(MenuDisplayOption.Recommendations),
          ...(settings.value?.recommendationsPageOptions?.menuDisplay instanceof Array
              ? (settings.value.recommendationsPageOptions
                  .menuDisplay as MenuDisplayOption[]).map((element: MenuDisplayOption) =>
                  menuItems.get(element)
              )
              : [
                MenuDisplayOption.FinancialDetails,
                MenuDisplayOption.CarbonFootprint,
                MenuDisplayOption.BerMap,
                MenuDisplayOption.ActionButton,
              ].map((option) => menuItems.get(option))),
        ].filter((value, i, self) => self.lastIndexOf(value) === i)
    );

    watch(route, updateItems);

    function updateItems() {
      items.value = items.value.map((item) => ({
        ...item,
        class:
            (item.class || "").replace(" active", "") +
            (route.name === item.to ? " active" : ""),
      }));
    }

    function getIcon(name: string): Component | undefined {
      switch (name) {
        case "recommendations":
          return RecommendationsIcon;
        case "financial-detail":
          return FinancialDetailsIcon;
        case "carbon-footprint":
          return CarbonFootprintIcon;
        case "ber-map":
          return BerMapIcon;
        default:
          return ScheduleSurveyIcon;
      }
    }

    const confirm = useConfirm();
    const showPopup = () => {
      confirm.require({
        header: t("startAgain.popupTitle"),
        message: t("startAgain.popupContent"),
        icon: "pi pi-exclamation-triangle",
        accept: () => {
          reset();
        },
      });
    };
    on(LayoutEvent.StartAgainClicked, (e: any) => {
      showPopup();
    });

    function reset() {
      passport.clear();

      if (!settings.value?.useWebHistory) {
        loan.$reset();
        recommendations.$reset();
        selections.$reset();
        settingsStore.setByBwId(false);
      }
      router.replace("/");
    }

    function linkItemClicked(item: string) {
      emit(BerwowEvent.TrackingEvent, {
        event: TrackingEvent.UserClickedTab,
        tab: item,
        timestamp: Date.now(),
        clientSessionId: clientSessionId.value,
      });
      scrollToContent();
    }

    function scrollToContent() {
      emit(LayoutEvent.ScrollToContent, undefined);
    }

    on(LayoutEvent.ExpandMenuBar, (expand: boolean) => expandMenu(expand));

    function expandMenu(expand: boolean) {
      if (!menuBarEl.value) return;

      const isActive = !!menuBarEl.value.$el.classList.contains(
          "p-menubar-mobile-active"
      );

      if (isActive !== expand) {
        menuBarEl.value.$el
            .getElementsByClassName("p-menubar-button")[0]
            .click();
        menuBarEl.value.unbindOutsideClickListener();
      }
    }

    return {
      items,
      route,
      getIcon,
      linkItemClicked,
      showPopup,
      menuBarEl,
      getIconFromCSS,
      MenuDisplayOption,
      settings,
      menuItems,
    };
  },
});
</script>
<style scoped lang="scss">
.p-menubar {
  //background-color: #004687;
  color: #fff;

  .p-menuitem-icon {
    height: 22px;
    width: 22px;
  }

  :deep(a) {
    text-decoration: none;
  }

  .berwow-icon {
    cursor: pointer;
    max-width: 100%;
    min-height: 22px;
  }

  :deep(.menubar-end) {
    display: none;
    justify-content: space-between;
    align-items: center;

    .berwow-icon {
      max-width: 175px;
      margin-right: 2rem;
      margin-left: 0.75rem;
    }

    :deep(button) {
      height: 3.25rem;
      max-width: 18rem;
    }
  }
}

@media screen and (max-width: 1200px) {
  .menubar-end {
    display: flex !important;

    :deep(button) {
      height: 3.25rem;
      max-width: 26rem;
    }
  }
  .p-menubar :deep(.p-menubar-end) {
    width: 100%;
    margin: 1rem;
  }
}

@media screen and (max-width: 640px) {
  .menubar-end .berwow-icon {
    max-width: 125px !important;
    margin-left: 0px;
  }
}
</style>
